/**
 * Tenant Redirect Logic
 *
 * Redirects subdomain visitors to custom domain if org has one configured.
 */

// Base domain from env (default for dev)
const BASE_DOMAIN = import.meta.env.VITE_BASE_DOMAIN || "wondermaps.co";
const API_URL = import.meta.env.VITE_API_URL || "";

// Reserved subdomains that should never redirect
const RESERVED_SUBDOMAINS = new Set(["www", "app", "api"]);

interface OrgRedirectResponse {
  customDomain: string | null;
}

/**
 * Check if current page should redirect to a custom domain.
 * Only runs on subdomains of baseDomain.
 * Returns true if redirecting, false otherwise.
 */
export async function checkAndRedirect(): Promise<boolean> {
  try {
    const hostname = window.location.hostname.toLowerCase();

    // Skip localhost
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return false;
    }

    // Skip if not a subdomain of base domain
    if (!hostname.endsWith(`.${BASE_DOMAIN}`)) {
      return false;
    }

    // Extract subdomain
    const subdomain = hostname.slice(0, -(BASE_DOMAIN.length + 1));

    // Skip multi-level subdomains
    if (subdomain.includes(".")) {
      return false;
    }

    // Skip reserved subdomains
    if (RESERVED_SUBDOMAINS.has(subdomain)) {
      return false;
    }

    // Query API for custom domain
    const response = await fetch(
      `${API_URL}/visitor/org-redirect?slug=${encodeURIComponent(subdomain)}`
    );

    if (!response.ok) {
      return false;
    }

    const data: OrgRedirectResponse = await response.json();

    if (!data.customDomain) {
      return false;
    }

    // Build redirect URL preserving path and query
    const { pathname, search } = window.location;
    const redirectUrl = `https://${data.customDomain}${pathname}${search}`;

    // Redirect using replace (no back button to subdomain)
    window.location.replace(redirectUrl);

    return true;
  } catch {
    // Fail silently on any error
    return false;
  }
}
